import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { products, contact } from '../../content';
import { getWhatsAppLink } from '../../content/contentUtils';

// Valores por defecto
const defaultProducts = {
  sectionLabel: "NUESTROS PRODUCTOS",
  sectionTitle: "Soluciones financieras a tu medida",
  sectionSubtitle: "Encuentra el crédito perfecto para tus necesidades",
  items: []
};

export const CreditsSection = () => {
  // Obtener contenido con fallbacks
  const content = {
    sectionLabel: products?.sectionLabel || defaultProducts.sectionLabel,
    sectionTitle: products?.sectionTitle || defaultProducts.sectionTitle,
    sectionSubtitle: products?.sectionSubtitle || defaultProducts.sectionSubtitle,
    items: products?.items || defaultProducts.items
  };

  const whatsappNumber = contact?.whatsapp?.number || "526561975200";

  // Function to generate WhatsApp link with custom message
  const getProductWhatsAppLink = (product) => {
    const message = product.whatsappMessage || `Hola, me interesa información del producto ${product.title}`;
    return getWhatsAppLink(message);
  };

  // Si no hay productos, no mostrar la sección
  if (content.items.length === 0) return null;

  return (
    <section id="creditos" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#CFA935] font-semibold text-sm uppercase tracking-wider">
            {content.sectionLabel}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#013C5D]">
            {content.sectionTitle}
          </h2>
          <p className="mt-4 text-gray-600 text-lg">
            {content.sectionSubtitle}
          </p>
        </div>

        {/* Credits Grid - First row: 3 products */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {content.items.slice(0, 3).map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#CFA935]/30"
            >
              {/* Product Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              {/* Card Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-[#013C5D] mb-2">
                  {product.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {product.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {(product.features || []).map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-[#CFA935]" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a href={getProductWhatsAppLink(product)} target="_blank" rel="noopener noreferrer">
                  <Button
                    className="w-full bg-[#013C5D] hover:bg-[#025a8a] text-white font-semibold group/btn"
                  >
                    {product.buttonText || "Solicitar ahora"}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Credits Grid - Second row: remaining products centered */}
        {content.items.length > 3 && (
          <div className="flex justify-center gap-8 flex-wrap">
            {content.items.slice(3).map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#CFA935]/30 w-full md:w-[calc(33.333%-1rem)] max-w-md"
              >
                {/* Product Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#013C5D] mb-2">
                    {product.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {product.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {(product.features || []).map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-[#CFA935]" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <a href={getProductWhatsAppLink(product)} target="_blank" rel="noopener noreferrer">
                    <Button
                      className="w-full bg-[#013C5D] hover:bg-[#025a8a] text-white font-semibold group/btn"
                    >
                      {product.buttonText || "Solicitar ahora"}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            ¿No sabes cuál es el mejor para ti?
          </p>
          <a href={getWhatsAppLink("Hola, necesito asesoría para elegir el mejor crédito para mí")} target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              className="bg-[#CFA935] hover:bg-[#b8942f] text-[#013C5D] font-semibold px-8"
            >
              Habla con un asesor
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};
